import React from "react";
import { PageHeader } from "../../field-service/components/PageHeader/PageHeader";
import { PartDetailDrawer } from "../../field-service/components/PartDetailDrawer/PartDetailDrawer";
import { useFinanceConfig } from "../../field-service/context/FinanceConfigContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type TechFeature = "HEAT-RESISTANT" | "STANDARD" | "HEAVY-DUTY";
type StorageLocation = "MAIN STORAGE" | "ON-SITE STOCK" | "MAINTENANCE ROOM" | "SPARE PART STORAGE";
type PartIcon = "scope" | "actuator-a" | "actuator-b" | "actuator-c" | "bearing";

interface Part {
  id: string;
  articleNumber: string;
  name: string;
  description: string;
  basePrice: number | null;
  availableStock: number | null;
  consumedStock: number | null;
  technicalFeatures: TechFeature | null;
  storageLocation: StorageLocation | null;
  iconType: PartIcon;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_PARTS: Part[] = [
  { id: "1",  articleNumber: "Test2",      name: "7URD 244 PLN SWE 400/230V",  description: "RECTIFIER FUSE",                  basePrice: 12.50,  availableStock: 0,    consumedStock: 0,    technicalFeatures: null,             storageLocation: null,                 iconType: "scope"      },
  { id: "2",  articleNumber: "PART-10021", name: "Actuator",                   description: "Heat-sealing packaging lines",     basePrice: 340.00, availableStock: 96,   consumedStock: 15,   technicalFeatures: "HEAT-RESISTANT", storageLocation: "MAIN STORAGE",       iconType: "actuator-a" },
  { id: "3",  articleNumber: "PART-10022", name: "Actuator",                   description: "Durable for high-speed usage",     basePrice: 295.00, availableStock: 76,   consumedStock: 4,    technicalFeatures: "STANDARD",       storageLocation: "MAIN STORAGE",       iconType: "actuator-a" },
  { id: "4",  articleNumber: "PART-10023", name: "Actuator",                   description: "Supports flexible materials",      basePrice: null,   availableStock: null, consumedStock: null, technicalFeatures: "HEAVY-DUTY",     storageLocation: "ON-SITE STOCK",      iconType: "actuator-b" },
  { id: "5",  articleNumber: "PART-10024", name: "Actuator",                   description: "Heat-sealing packaging lines",     basePrice: 310.00, availableStock: 73,   consumedStock: 7,    technicalFeatures: "HEAT-RESISTANT", storageLocation: "MAINTENANCE ROOM",   iconType: "actuator-b" },
  { id: "6",  articleNumber: "PART-10025", name: "Actuator",                   description: "Durable for high-speed usage",     basePrice: null,   availableStock: null, consumedStock: null, technicalFeatures: "STANDARD",       storageLocation: "ON-SITE STOCK",      iconType: "actuator-a" },
  { id: "7",  articleNumber: "PART-10026", name: "Actuator",                   description: "Heat-sealing packaging lines",     basePrice: 285.00, availableStock: null, consumedStock: null, technicalFeatures: "HEAT-RESISTANT", storageLocation: "SPARE PART STORAGE", iconType: "actuator-c" },
  { id: "8",  articleNumber: "PART-10027", name: "Actuator",                   description: "Heat-sealing packaging lines",     basePrice: 285.00, availableStock: null, consumedStock: null, technicalFeatures: "HEAT-RESISTANT", storageLocation: "SPARE PART STORAGE", iconType: "actuator-c" },
  { id: "9",  articleNumber: "PART-10028", name: "Actuator",                   description: "Supports flexible materials",      basePrice: 320.00, availableStock: 32,   consumedStock: 18,   technicalFeatures: "HEAVY-DUTY",     storageLocation: "SPARE PART STORAGE", iconType: "actuator-b" },
  { id: "10", articleNumber: "PART-10029", name: "Bearing Set",                description: "Heat-sealing packaging lines",     basePrice: 54.00,  availableStock: 8,    consumedStock: 12,   technicalFeatures: "HEAT-RESISTANT", storageLocation: "MAIN STORAGE",       iconType: "bearing"    },
  { id: "11", articleNumber: "PART-10030", name: "Bearing Set",                description: "Durable for high-speed usage",     basePrice: 48.00,  availableStock: 0,    consumedStock: 0,    technicalFeatures: "STANDARD",       storageLocation: "MAINTENANCE ROOM",   iconType: "bearing"    },
  { id: "12", articleNumber: "PART-10031", name: "Bearing Set",                description: "Supports flexible materials",      basePrice: null,   availableStock: 44,   consumedStock: 9,    technicalFeatures: "HEAVY-DUTY",     storageLocation: "SPARE PART STORAGE", iconType: "bearing"    },
];

// ─── Badge configs ────────────────────────────────────────────────────────────

const TECH_FEATURE_CONFIG: Record<TechFeature, { bg: string; color: string }> = {
  "HEAT-RESISTANT": { bg: "#fce7f3", color: "#be185d" },
  "STANDARD":       { bg: "#1f2937", color: "#ffffff" },
  "HEAVY-DUTY":     { bg: "#ede9fe", color: "#7c3aed" },
};

const STORAGE_LOCATION_CONFIG: Record<StorageLocation, { bg: string; color: string }> = {
  "MAIN STORAGE":       { bg: "#fef3c7", color: "#b45309" },
  "ON-SITE STOCK":      { bg: "#f3f4f6", color: "#6b7280" },
  "MAINTENANCE ROOM":   { bg: "#ccfbf1", color: "#0f766e" },
  "SPARE PART STORAGE": { bg: "#f1f5f9", color: "#64748b" },
};

// ─── Badges ───────────────────────────────────────────────────────────────────

function TechFeatureBadge({ value }: { value: TechFeature }) {
  const cfg = TECH_FEATURE_CONFIG[value];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
      background: cfg.bg, color: cfg.color,
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {value}
    </span>
  );
}

function StorageLocationBadge({ value }: { value: StorageLocation }) {
  const cfg = STORAGE_LOCATION_CONFIG[value];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
      background: cfg.bg, color: cfg.color,
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {value}
    </span>
  );
}

// ─── Row Icons ────────────────────────────────────────────────────────────────

function ScopeIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#e6e8fe" />
      <circle cx="16" cy="16" r="7" stroke="#0517f8" strokeWidth="1.5" fill="none" />
      <circle cx="16" cy="16" r="3" stroke="#0517f8" strokeWidth="1.5" fill="none" />
      <circle cx="16" cy="16" r="1.5" fill="#0517f8" />
      <path d="M16 6v4M16 22v4M6 16h4M22 16h4" stroke="#0517f8" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ActuatorIconA() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#f1f5f9" />
      <rect x="6" y="12" width="14" height="8" rx="2" fill="#94a3b8" />
      <rect x="18" y="14" width="8" height="4" rx="1" fill="#64748b" />
      <circle cx="10" cy="16" r="2" fill="#475569" />
      <rect x="6" y="10" width="4" height="3" rx="1" fill="#64748b" />
      <rect x="6" y="19" width="4" height="3" rx="1" fill="#64748b" />
    </svg>
  );
}

function ActuatorIconB() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#f1f5f9" />
      <circle cx="16" cy="16" r="7" fill="#94a3b8" />
      <circle cx="16" cy="16" r="4" fill="#64748b" />
      <circle cx="16" cy="16" r="2" fill="#475569" />
      <rect x="8" y="14" width="16" height="4" rx="1" fill="#475569" opacity="0.4" />
    </svg>
  );
}

function ActuatorIconC() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#f1f5f9" />
      <rect x="8" y="8" width="16" height="16" rx="3" fill="#94a3b8" />
      <rect x="11" y="11" width="10" height="10" rx="2" fill="#64748b" />
      <rect x="13" y="13" width="6" height="6" rx="1" fill="#475569" />
      <circle cx="16" cy="16" r="1.5" fill="#e2e8f0" />
    </svg>
  );
}

function BearingIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#f1f5f9" />
      <circle cx="16" cy="16" r="8" stroke="#94a3b8" strokeWidth="2.5" fill="none" />
      <circle cx="16" cy="16" r="4.5" stroke="#64748b" strokeWidth="2" fill="none" />
      <circle cx="16" cy="16" r="2" fill="#475569" />
      <circle cx="16" cy="8.5" r="1.5" fill="#94a3b8" />
      <circle cx="16" cy="23.5" r="1.5" fill="#94a3b8" />
      <circle cx="8.5" cy="16" r="1.5" fill="#94a3b8" />
      <circle cx="23.5" cy="16" r="1.5" fill="#94a3b8" />
    </svg>
  );
}

const ROW_ICONS: Record<PartIcon, React.ReactNode> = {
  "scope":      <ScopeIcon />,
  "actuator-a": <ActuatorIconA />,
  "actuator-b": <ActuatorIconB />,
  "actuator-c": <ActuatorIconC />,
  "bearing":    <BearingIcon />,
};

// ─── Column Icons ─────────────────────────────────────────────────────────────

function ArticleNumberIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 2v10M9 2v10M2 5h10M2 9h10" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5" />
      <circle cx="7" cy="7" r="2.5" />
      <path d="M7 1v2M7 11v2M1 7h2M11 7h2" />
    </svg>
  );
}

function DescriptionIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3.5h10M2 7h10M2 10.5h6" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 8.5a3.536 3.536 0 004.95 0l1.5-1.5a3.536 3.536 0 10-5-5L6 3" />
      <path d="M8.5 5.5a3.536 3.536 0 00-4.95 0L2 7a3.536 3.536 0 105 5l.95-.95" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <path d="M6 2L8.5 5H3.5L6 2Z" fill="currentColor" />
      <path d="M6 10L3.5 7H8.5L6 10Z" fill="currentColor" />
    </svg>
  );
}

function TableViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="1.5" />
      <path d="M2 6h12M6 6v8" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 1.5V3M8 13v1.5M1.5 8H3M13 8h1.5M3.05 3.05l1.06 1.06M11.89 11.89l1.06 1.06M3.05 12.95l1.06-1.06M11.89 4.11l1.06-1.06" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8M5 7l3 3 3-3" />
      <path d="M3 11v1.5A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5V11" />
    </svg>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface PartsListPageProps {
  onAddAsset?: () => void;
}

const COLUMNS = [
  { key: "articleNumber",    label: "Article Number",    icon: <ArticleNumberIcon />, sortable: true,  width: 160 },
  { key: "name",             label: "Name",              icon: <TargetIcon />,        sortable: true,  width: 160 },
  { key: "description",      label: "Description",       icon: <DescriptionIcon />,   sortable: false, width: 200 },
  { key: "basePrice",        label: "Price",             icon: <LinkIcon />,          sortable: true,  width: 120 },
  { key: "availableStock",   label: "Available Stock",   icon: <LinkIcon />,          sortable: true,  width: 140 },
  { key: "consumedStock",    label: "Consumed Stock",    icon: <LinkIcon />,          sortable: true,  width: 140 },
  { key: "technicalFeatures",label: "Technical Features",icon: <LinkIcon />,          sortable: false, width: 180 },
  { key: "storageLocation",  label: "Storage Location",  icon: <LinkIcon />,          sortable: false, width: 200 },
] as const;

const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: "€", USD: "$", GBP: "£", JPY: "¥", CHF: "Fr",
};
function currencySymbol(code: string) { return CURRENCY_SYMBOL[code] ?? code; }

export function PartsListPage({ onAddAsset }: PartsListPageProps) {
  const { config: financeConfig } = useFinanceConfig();
  const symbol = currencySymbol(financeConfig.currency);
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
  const [sortCol, setSortCol] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");
  const [selectedPart, setSelectedPart] = React.useState<Part | null>(null);

  const sortedRows = React.useMemo(() => {
    if (!sortCol) return MOCK_PARTS;
    return [...MOCK_PARTS].sort((a, b) => {
      const va = String((a as unknown as Record<string, unknown>)[sortCol] ?? "");
      const vb = String((b as unknown as Record<string, unknown>)[sortCol] ?? "");
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [sortCol, sortDir]);

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  const colHeaderStyle: React.CSSProperties = {
    background: "var(--color-bg-accent)",
    borderBottom: "1px solid var(--color-border-primary)",
    padding: "0 12px",
    height: 34,
    whiteSpace: "nowrap",
    textAlign: "left",
    fontWeight: "normal",
  };

  const colHeaderInner: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    color: "#748094",
    cursor: "pointer",
    userSelect: "none",
    fontFamily: "var(--font-family-base)",
  };

  const cellStyle: React.CSSProperties = {
    padding: "0 12px",
    height: 56,
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-content-primary)",
    fontFamily: "var(--font-family-base)",
    borderBottom: "1px solid var(--color-border-primary)",
    verticalAlign: "middle",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Page header */}
      <PageHeader
        breadcrumbs={[{ label: "Parts" }]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              aria-label="Search"
              style={{ width: 40, height: 40, background: "var(--color-bg-accent)", border: "none", borderRadius: "var(--radius-s)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667Z" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 14l-2.9-2.9" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              aria-label="Import"
              style={{ width: 40, height: 40, background: "var(--color-bg-accent)", border: "none", borderRadius: "var(--radius-s)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <ImportIcon />
            </button>
            <button
              onClick={onAddAsset}
              style={{ height: 40, padding: "0 16px", background: "var(--color-bg-brand-dark)", border: "none", borderRadius: "var(--radius-s)", fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", color: "var(--color-content-inverse)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Add Assets
            </button>
          </div>
        }
      />

      <div style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-primary)" }}>

        {/* View & filter bar */}
        <div style={{ height: 60, flexShrink: 0, background: "var(--color-bg-primary)", borderBottom: "1px solid var(--color-border-primary)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px" }}>
          <button style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 8px", borderRadius: 4, border: "none", cursor: "pointer", background: "#e6e8fe", fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px", color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
            <TableViewIcon active={true} />
            Table
          </button>
          <button style={{ width: 34, height: 34, border: "1px solid var(--color-border-primary)", borderRadius: 8, background: "var(--color-bg-primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SettingsIcon />
          </button>
        </div>

        {/* Table */}
        <div style={{ margin: "24px", border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 1380 }}>
              <colgroup>
                {COLUMNS.map((col) => (
                  <col key={col.key} style={{ width: col.width }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {COLUMNS.map(({ key, label, icon, sortable }) => (
                    <th key={key} style={colHeaderStyle}>
                      <span style={colHeaderInner} onClick={() => sortable && handleSort(key)}>
                        {icon}
                        {label}
                        {sortable && <SortIcon />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => setSelectedPart(row)}
                  style={{ background: hoveredRow === row.id ? "var(--color-bg-accent)" : "var(--color-bg-primary)", cursor: "pointer", transition: "background 80ms ease" }}
                  >
                    {/* Article Number */}
                    <td style={cellStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {ROW_ICONS[row.iconType]}
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.articleNumber}</span>
                      </div>
                    </td>
                    {/* Name */}
                    <td style={cellStyle}>{row.name}</td>
                    {/* Description */}
                    <td style={{ ...cellStyle, color: "#748094" }}>{row.description}</td>
                    {/* Base Price */}
                    <td style={cellStyle}>
                      {row.basePrice != null
                        ? <span>{symbol}{row.basePrice.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        : <span style={{ color: "#a2aab8" }}>-</span>
                      }
                    </td>
                    {/* Available Stock */}
                    <td style={cellStyle}>{row.availableStock ?? "-"}</td>
                    {/* Consumed Stock */}
                    <td style={cellStyle}>{row.consumedStock ?? "-"}</td>
                    {/* Technical Features */}
                    <td style={{ ...cellStyle, overflow: "visible" }}>
                      {row.technicalFeatures ? <TechFeatureBadge value={row.technicalFeatures} /> : <span style={{ color: "#a2aab8" }}>-</span>}
                    </td>
                    {/* Storage Location */}
                    <td style={{ ...cellStyle, overflow: "visible" }}>
                      {row.storageLocation ? <StorageLocationBadge value={row.storageLocation} /> : <span style={{ color: "#a2aab8" }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedRows.length === 0 && (
              <div style={{ textAlign: "center", padding: "48px 24px", color: "var(--color-content-tertiary)", fontSize: 14, fontFamily: "var(--font-family-base)" }}>
                No parts found
              </div>
            )}
          </div>
        </div>
      </div>

      <PartDetailDrawer
        open={selectedPart !== null}
        part={selectedPart}
        onClose={() => setSelectedPart(null)}
      />
    </div>
  );
}
